import './ScreenManager.scss';
import { Container } from '@components/Component/Container/Container.js';
import { ScreenGroup } from '@components/Component/Container/ScreenGroup/ScreenGroup.js';
import { Form } from '@components/Component/Container/Form/Form.js';
import { getID, isMobile } from '@components/Utility/index.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
/**
 * The `ScreenManager` is an abstract class that provides a base structure for managing screens.
 * It includes methods to add, remove, activate and get screens and screen groups.
 */
export class ScreenManager extends Container {
    constructor() {
        super(...arguments);
        this.componentName = 'ScreenManager';
        /** Map of screens identified by their unique IDs. */
        this.screens = new Map();
        /** Map of screen groups identified by their unique IDs. */
        this.screenGroups = new Map();
        /** ID of the currently active screen. */
        this.activeScreenId = '';
        this._screenCoverActive = false;
    }
    get screenCoverActive() {
        return this._screenCoverActive;
    }
    set screenCoverActive(value) {
        this._propertySet('screenCoverActive', value);
    }
    initializeComponents() {
        super.initializeComponents();
        const screenCover = DIContainer.getInstance('ScreenCover', 'screencover');
        this.componentsAdd([screenCover]);
    }
    /**
     * Opens a new screen within a screen group.
     *
     * @param url - The URL to open in the screen. Also can pass a Form component to immediately open an instance of a form in a screen.
     * @param options - ScreenManagerOpenOptionsInterface - options to affect how the screen will open.
     * @returns The screen that was opened.
     */
    open(url, options) {
        if (isMobile() || options?.collapseToolbar) {
            DIContainer.getInstance('Application').componentGet('toolbar')?.propertySet('collapsed', true);
        }
        const defaultOptions = {
            screenGroupId: '',
            title: '',
            hash: '',
            delayUntilRefreshed: false,
            screenCover: false,
            ...options // merge requested options 
        };
        const screen = defaultOptions.screenCover
            ? this.screenCoverGet()
            : this.screenCreate(defaultOptions.screenGroupId);
        screen.propertySet('title', defaultOptions.title);
        this.activeScreenSet(screen.id);
        if (url instanceof Form) {
            screen.loadForm(url);
            if (!defaultOptions.skipRefreshData) {
                url.refreshData(defaultOptions.requestParams);
            }
        }
        else {
            screen.navigate(url, defaultOptions.requestParams, { hash: defaultOptions.hash, delayUntilRefreshed: defaultOptions.delayUntilRefreshed });
        }
        return screen;
    }
    /**
     * Gets the count of the current screens.
     *
     * @returns The number of screens.
     */
    screenCountGet() {
        return this.screens.size;
    }
    /**
     * Provides a list of existing screens in the format {id, title} for use in a dropdown
     */
    screensList() {
        const screens = [];
        this.screens.forEach((screen, key) => {
            screens.push({ id: key, title: screen.title });
        });
        return screens;
    }
    /**
     * Creates a new screen group.
     *
     * @returns The created screen group.
     */
    screenGroupCreate() {
        const screenGroupRoot = document.createElement('div');
        const screenGroup = new ScreenGroup(getID('screenGroup'), screenGroupRoot);
        screenGroup.draw();
        this.screenGroupAdd(screenGroup);
        return screenGroup;
    }
    /**
     * Adds a screen group.
     *
     * @param screenGroup - The screen group to be added.
     */
    screenGroupAdd(screenGroup) {
        this.screenGroups.set(screenGroup.id, screenGroup);
        this.rootElement.appendChild(screenGroup.rootElement);
    }
    /**
     * Adds a screen.
     *
     * @param screen - The screen to be added.
     */
    screenAdd(screen) {
        this.screens.set(screen.id, screen);
    }
    screenCoverGet() {
        let screenCover = this.componentGet('screencover');
        if (!screenCover) {
            // fail safe
            screenCover = DIContainer.getInstance('ScreenCover', 'screencover');
            this.componentAdd(screenCover);
        }
        this.screenAdd(screenCover);
        this.screenCoverActive = true;
        return screenCover;
    }
    /**
     * Sets the active screen.
     *
     * @param id - The ID of the screen to be activated.
     */
    activeScreenSet(id) {
        this.activeScreenId = id;
        const screen = this.screens.get(id);
        if (screen) {
            const screenGroup = this.screenGroups.get(screen.propertyGet('screenGroupId'));
            if (screenGroup) {
                this.activeScreenGroupSet(screenGroup.id);
                screenGroup.activeScreenSet(id);
            } /*else {
                this.removeActiveFromAllScreens();
                
                screen.rootElement.classList.add('active');
            }*/
        }
        else {
            this.removeActiveFromAllScreens();
        }
    }
    activateNextScreen() {
        // Try to activate next screen within the active screen group first
        const activeScreenGroup = this.activeScreenGroupGet();
        if (activeScreenGroup && activeScreenGroup.screenCountGet() > 0) {
            return activeScreenGroup.activateNextScreen();
        }
        // Fallback: use z-index to find next screen across all screens
        let maxZIndex = -Infinity;
        let nextScreen;
        this.screens.forEach((screen, id) => {
            const zIndex = parseInt(window.getComputedStyle(screen.rootElement).zIndex, 10) || 0;
            if (id !== this.activeScreenId && zIndex > maxZIndex) {
                maxZIndex = zIndex;
                nextScreen = screen;
            }
        });
        if (nextScreen) {
            this.activeScreenSet(nextScreen.id);
        }
        return nextScreen;
    }
    activatePreviousScreen() {
        // Try to activate previous screen within the active screen group first
        const activeScreenGroup = this.activeScreenGroupGet();
        if (activeScreenGroup && activeScreenGroup.screenCountGet() > 0) {
            return activeScreenGroup.activatePreviousScreen();
        }
        // Fallback: use z-index to find previous screen across all screens
        let minZIndex = Infinity;
        let previousScreen;
        this.screens.forEach((screen, id) => {
            const zIndex = parseInt(window.getComputedStyle(screen.rootElement).zIndex, 10) || 0;
            if (id !== this.activeScreenId && zIndex < minZIndex) {
                minZIndex = zIndex;
                previousScreen = screen;
            }
        });
        if (previousScreen) {
            this.activeScreenSet(previousScreen.id);
        }
        return previousScreen;
    }
    /**
     * Removes the 'active' state from all screens.
     */
    removeActiveFromAllScreens() {
        // Remove active class from all screens
        this.screens.forEach(screen => {
            screen.activeSet(false);
        });
    }
    /**
     * Sets the active screen group.
     *
     * @param id - The ID of the screen group to be activated.
     */
    activeScreenGroupSet(id) {
        // Remove active class from all screenGroups
        this.screenGroups.forEach(screenGroup => {
            screenGroup.activeSet(id === screenGroup.id);
        });
    }
    /**
     * Gets the currently active screen group.
     *
     * @returns The currently active screen group.
     */
    activeScreenGroupGet() {
        const screen = this.activeScreenGet();
        const screenGroupId = screen ? screen.propertyGet('screenGroupId') : '';
        let screenGroup;
        if (screenGroupId) {
            screenGroup = this.screenGroups.get(screenGroupId);
        }
        if (!screenGroup) {
            screenGroup = this.screenGroups.values().next().value;
            if (!screenGroup) {
                screenGroup = this.screenGroupCreate();
            }
        }
        return screenGroup;
    }
    /**
     * Gets the currently active screen.
     *
     * @returns The currently active screen, or undefined if none is active.
     */
    activeScreenGet() {
        return this.screens.get(this.activeScreenId);
    }
    /**
     * Gets a screen by its ID.
     *
     * @param id - The ID of the screen.
     * @returns The screen, or undefined if no screen is found with the given ID.
     */
    screenGet(id) {
        return this.screens.get(id);
    }
    /**
     * Gets a screen group by its ID.
     *
     * @param id - The ID of the screen group.
     * @returns The screen group, or undefined if no screen group is found with the given ID.
     */
    screenGroupGet(id) {
        return this.screenGroups.get(id);
    }
    /**
     * Removes a screen.
     *
     * @param id - The ID of the screen to be removed.
     */
    screenRemove(id) {
        const screen = this.screens.get(id);
        if (screen) {
            const screenGroup = this.screenGroups.get(screen.propertyGet('screenGroupId'));
            if (screenGroup) {
                screenGroup.screenRemove(id);
            }
            if (id === this.activeScreenId) {
                this.activeScreenId = '';
            }
            this.screens.delete(id);
        }
    }
    /**
     * Moves a screen to a different group.
     *
     * @param screenID - The ID of the screen to be moved.
     * @param groupID - The ID of the group to move the screen to.
     */
    moveScreenToGroup(screenID, groupID) {
        const screen = this.screenGet(screenID);
        if (!screen) {
            return;
        }
        const currentGroupID = screen.propertyGet('screenGroupId');
        const currentScreenGroup = this.screenGroupGet(currentGroupID);
        if (currentScreenGroup && !groupID && currentScreenGroup.screenCountGet() <= 1) {
            // group only has one item currently. Dont bother trying to create a new group. 
            return;
        }
        const newScreenGroup = this.screenGroupGet(groupID) || this.screenGroupCreate();
        if (currentScreenGroup) {
            currentScreenGroup.screenRemove(screenID);
        }
        newScreenGroup.screenAdd(screen);
    }
    forEachScreen(callback) {
        this.screens.forEach(callback);
    }
    forEachScreenGroup(callback) {
        this.screenGroups.forEach(callback);
    }
    /**
     * Removes a screen group.
     *
     * @param id - The ID of the screen group to be removed.
     */
    screenGroupRemove(id) {
        const screenGroup = this.screenGroupGet(id);
        if (screenGroup) {
            this.rootElement.removeChild(screenGroup.rootElement);
            this.screenGroups.delete(id);
        }
    }
    template() {
        return '<div data-componentid="screencover"></div>';
    }
    generateChildTemplate() {
        const template = '';
        return template;
    }
}
ScreenManager.VERSION = '$Revision: 130642 $';
/** TODO: Specifies the limit of screen groups that can be created. (not yet implemented) */
ScreenManager.screenGroupLimit = 4;
